import { useEffect } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/Breadcrumbs";
import PageSchema from "@/components/PageSchema";
import LanguageSwitchLink from "@/components/LanguageSwitchLink";
import { setPageMeta, setHreflang } from "@/lib/seo";

const EN_URL = "/towing-cdl";
const ES_URL = "/es/licencia-cdl-remolques";

export default function LicenciaCdlRemolquesPage() {
  useEffect(() => {
    document.documentElement.lang = "es";
    setPageMeta(
      "CDL y Remolcado: Cuándo Necesitas Licencia Comercial | Juan The Trailer Guy",
      "¿Cuándo necesitas un CDL para remolcar? Explicamos el límite de 26,001 lbs, el cálculo de GCWR, gooseneck sin CDL y los requisitos de hotshot.",
      ES_URL,
    );
    const cleanup = setHreflang([
      { lang: "en-US", href: EN_URL },
      { lang: "es-US", href: ES_URL },
      { lang: "x-default", href: EN_URL },
    ]);
    return () => {
      document.documentElement.lang = "en";
      cleanup();
    };
  }, []);

  return (
    <main className="bg-background text-foreground">
      <PageSchema
        name="CDL y Remolcado: Cuándo Necesitas Licencia Comercial"
        description="Explicamos el límite de 26,001 lbs, el cálculo de GCWR, gooseneck sin CDL y los requisitos de hotshot."
      />
      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
          <Breadcrumbs
            variant="dark"
            className="mb-5"
            items={[{ name: "Inicio", href: "/" }, { name: "Guías Educativas" }, { name: "Licencia CDL para Remolques" }]}
          />
          <p className="text-sm uppercase tracking-[0.2em] text-red-400 font-semibold">CDL y Remolcado</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-black uppercase leading-tight">
            CDL y Remolcado: Cuándo Necesitas Licencia Comercial
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-zinc-300">
            Una de las preguntas más comunes entre quienes jalan tráileres: ¿necesito un CDL (Commercial
            Driver's License) para esto? Aquí te explicamos las reglas básicas, el límite de 26,001 libras
            y lo que cambia cuando trabajas de forma comercial, como en hotshot.
          </p>
          <div className="mt-6">
            <LanguageSwitchLink href={EN_URL} label="Read this guide in English" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-14">
        <div className="rounded-xl border border-border bg-muted/50 p-6">
          <p className="mb-1 text-sm font-semibold uppercase tracking-widest text-primary">Respuesta Rápida</p>
          <p className="text-lg leading-relaxed text-foreground">
            En general, necesitas un CDL cuando el <strong>GCWR (peso combinado de la troca más el
            remolque)</strong> supera las <strong>26,001 libras</strong> y el remolque pesa más de 10,000
            libras. Si tu combinación se queda por debajo de ese límite, normalmente puedes jalar con una
            licencia regular de manejar.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-6 text-lg leading-8 text-muted-foreground">
        <h2 className="text-2xl font-black uppercase text-foreground">El Límite de las 26,001 Libras</h2>
        <p className="mt-4">
          La regla federal básica dice que si el peso combinado bruto de tu troca y tu remolque (el GCWR —
          Gross Combination Weight Rating) pasa de 26,001 libras, y el remolque por sí solo pesa más de
          10,000 libras, necesitas un CDL de Clase A. Esto aplica aunque tu troca sea una pickup "normal" —
          lo que importa es el peso combinado, no si se ve como un camión comercial.
        </p>
        <p className="mt-4">
          Importante: esto se calcula con el <strong>GVWR</strong> (peso bruto máximo permitido) de cada
          vehículo, no con lo que realmente pesan vacíos. Muchas trocas pesadas (3/4 tonelada o 1 tonelada)
          ya tienen un GVWR alto, así que al sumarle un remolque cargado, fácilmente puedes pasar el
          límite sin darte cuenta.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">Cómo se Calcula el GCWR</h2>
        <p className="mt-4">
          El cálculo es simple en teoría: suma el GVWR de tu troca más el GVWR del remolque. Si el total
          pasa de 26,001 libras y el remolque pesa más de 10,000 libras, entras en territorio de CDL.
        </p>
        <ul className="mt-4 space-y-2">
          <li>• GVWR de la troca: el peso máximo permitido de la troca, cargada</li>
          <li>• GVWR del remolque: el peso máximo permitido del remolque, cargado</li>
          <li>• GCWR: la suma de ambos — este es el número que importa para el CDL</li>
        </ul>
        <p className="mt-4">
          Usa nuestra{" "}
          <Link to="/trailer-payload-calculator" className="font-semibold text-primary underline">
            calculadora de carga y remolcado
          </Link>{" "}
          (en inglés) para hacer este cálculo con tu propia troca y remolque.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">Goosenecks sin CDL</h2>
        <p className="mt-4">
          Mucha gente arma su combinación específicamente para quedarse por debajo del límite de 26,001
          libras y así evitar el CDL — sobre todo con remolques gooseneck para equipo o volteo. Esto es
          legal y común, pero significa que tienes menos margen de capacidad de carga. Si tu negocio está
          creciendo, vale la pena pensar si conviene más capacidad (y obtener el CDL) en lugar de quedarte
          limitado.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">Requisitos para Hotshot</h2>
        <p className="mt-4">
          El trabajo de hotshot (jalar cargas con una pickup de uso pesado y un remolque gooseneck o de
          plataforma) tiene sus propias reglas según si operas de manera comercial. Si tu GCWR pasa de
          26,001 libras, generalmente necesitas: CDL Clase A, número de USDOT, y posiblemente autoridad de
          operador (operating authority) si cobras por transportar la carga de otra persona. Las reglas
          exactas varían según el estado y si cruzas líneas estatales, así que siempre confirma con tu DMV
          local o el DOT.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">¿Y los Remolques Roll-Off?</h2>
        <p className="mt-4">
          Los remolques roll-off (contenedores basculantes) suelen ser más pesados que un dump trailer
          normal, así que es común que su combinación sí pase el límite de 26,001 libras, especialmente
          cargados. Si vas a operar un negocio de roll-off, revisa el peso real de tu combinación antes de
          asumir que no necesitas CDL.
        </p>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">Sigue Aprendiendo</h2>
          <div className="mt-6 text-primary font-semibold space-y-2">
            <Link to="/es/gvwr-capacidad-de-carga" className="block hover:underline">→ GVWR y Capacidad de Carga Explicados</Link>
            <Link to="/es/remolques-de-volteo" className="block hover:underline">→ Tráileres de Volteo Explicadas</Link>
            <Link to="/es/costos-de-tener-un-remolque" className="block hover:underline">→ Costos Reales de Tener un Remolque</Link>
            <Link to="/trailer-payload-calculator" className="block hover:underline">→ Calculadora de GVWR y Carga (en inglés)</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
